import { cn } from "@/lib/utils";
import { format, isSameDay, isSameMonth, isToday, type Locale } from "date-fns";
import { ChevronRight } from "lucide-react";
import { useTranslate } from "ra-core";

import { AgendaChip } from "./AgendaWeek";
import { dayKey } from "./mobileModel";
import { SOURCE_BAR, type AgendaEntry } from "./useAgendaEntries";

const MAX_DOTS = 3;

/**
 * Month grid with a dot per item (up to three) and a count; tap opens Day.
 * On a phone the selected day is previewed under the grid.
 */
export const AgendaMonth = ({
  days,
  date,
  byDay,
  dfLocale,
  locale,
  onOpenDay,
}: {
  days: Date[];
  date: Date;
  byDay: Map<string, AgendaEntry[]>;
  dfLocale: Locale;
  locale: string;
  onOpenDay: (day: Date) => void;
}) => {
  const translate = useTranslate();
  const preview = byDay.get(dayKey(date)) ?? [];
  return (
    <>
      <div className="overflow-hidden rounded-4xl bg-card p-2 shadow-card ring-1 ring-border md:p-3">
        <div
          className="grid grid-cols-7 pb-1 text-center text-[11px] font-medium tracking-wide text-muted-foreground uppercase md:text-left"
          aria-hidden
        >
          {days.slice(0, 7).map((day) => (
            <span key={dayKey(day)} className="py-1 md:px-2.5">
              {format(day, "EEEEEE", { locale: dfLocale })}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0.5 md:gap-1">
          {days.map((day) => {
            const key = dayKey(day);
            const items = byDay.get(key) ?? [];
            const inMonth = isSameMonth(day, date);
            const selected = isSameDay(day, date);
            const today = isToday(day);
            const label = [
              format(day, "PPPP", { locale: dfLocale }),
              items.length > 0
                ? translate("crm.mobile.agenda_items", {
                    smart_count: items.length,
                  })
                : null,
            ]
              .filter(Boolean)
              .join(", ");
            return (
              <button
                key={key}
                type="button"
                onClick={() => onOpenDay(day)}
                aria-label={label}
                aria-current={today ? "date" : undefined}
                className={cn(
                  "flex aspect-square min-w-0 flex-col items-center justify-start gap-1 rounded-2xl pt-1.5 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring md:aspect-auto md:min-h-24 md:items-start md:px-2.5 md:pt-2",
                  "hover:bg-muted/60",
                  !inMonth && "text-muted-foreground/50",
                )}
              >
                <span
                  className={cn(
                    "inline-flex size-7 items-center justify-center rounded-full text-sm tabular-nums",
                    today && "agenda-accent font-semibold",
                    selected && !today && "font-semibold ring-1 ring-brand/50",
                  )}
                >
                  {format(day, "d")}
                </span>
                {items.length > 0 ? (
                  <span className="flex items-center gap-0.5 md:gap-1">
                    {items.slice(0, MAX_DOTS).map((entry) => (
                      <span
                        key={entry.id}
                        className={cn(
                          "size-1.5 rounded-full",
                          SOURCE_BAR[entry.source] ?? "bg-chart-1",
                          !inMonth && "opacity-50",
                        )}
                      />
                    ))}
                    {items.length > MAX_DOTS ? (
                      <span className="ml-0.5 text-[10px] leading-none text-muted-foreground tabular-nums md:hidden">
                        +{items.length - MAX_DOTS}
                      </span>
                    ) : null}
                    <span className="ml-1 hidden text-xs text-muted-foreground tabular-nums md:inline">
                      {items.length}
                    </span>
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
      <section className="mt-5 md:hidden">
        <button
          type="button"
          onClick={() => onOpenDay(date)}
          className="mb-2 flex w-full items-center gap-1 rounded-2xl px-1 py-1 text-left text-sm font-semibold outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="first-letter:uppercase">
            {format(date, "EEEE, d MMMM", { locale: dfLocale })}
          </span>
          <ChevronRight className="size-4 text-muted-foreground" />
        </button>
        {preview.length === 0 ? (
          <p className="px-1 text-sm text-muted-foreground">
            {translate("crm.mobile.no_events")}
          </p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {preview.map((entry) => (
              <li key={entry.id}>
                <AgendaChip entry={entry} locale={locale} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
};
